import { z } from 'zod';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { WorkflowDraftDefinition } from '@/lib/api/generated/model';
import { guardedLocalStorage } from '@/shared/store/guardedLocalStorage';
import {
  BUILDER_BACKUPS_STORAGE_KEY,
  registerPersonalDraftStore,
} from '@/shared/store/personalDrafts';
import { isDraftFresh } from '@/shared/store/formDrafts';

import { fromWorkflowView, withDefinition } from './fromView';
import { toDraftDefinition } from './toRequest';
import type { WorkflowDraft } from './types';

export { BUILDER_BACKUPS_STORAGE_KEY };

/**
 * This browser's copy of one workflow's unsaved edits (spec 144 FR-008).
 *
 * The safety net behind the leave dialog, for the exits it cannot see: a
 * closed tab, a crash, a laptop lid. Never sent anywhere — it is offered back
 * in the builder, and only a Save puts it on the server.
 */
export interface BuilderBackup {
  /** The edits, in the shape a save would send. */
  definition: WorkflowDraftDefinition;
  /** When the edits were last backed up. */
  savedAt: string;
  /**
   * What the edits were made on top of: the pending changes' revision, if
   * there were any, and the published or draft version.
   */
  basedOn: { revision: string | null; version: number };
}

interface BuilderBackupState {
  /**
   * By the person who made them, then by workflow id (ids are unique across
   * workspaces).
   *
   * Keyed by person because a browser can be shared: a backup is only ever
   * offered back to whoever made it, never to the next person to sign in.
   */
  backups: Record<string, Record<string, BuilderBackup>>;
  keep: (owner: string, workflowId: string, backup: BuilderBackup) => void;
  drop: (owner: string, workflowId: string) => void;
  /** Every backup, everyone's: the browser is being handed back. */
  clearAll: () => void;
}

/**
 * One backup per person and workflow, persisted through storage that never
 * throws — the same mechanism the spec 121 and 123 draft stores use. Where
 * storage is refused, a backup lives as long as the tab and the builder
 * carries on without saying so.
 */
export const useBuilderBackupStore = create<BuilderBackupState>()(
  persist(
    (set, get) => ({
      backups: {},
      keep: (owner, workflowId, backup) => {
        const backups = get().backups;
        set({
          backups: {
            ...backups,
            [owner]: { ...backups[owner], [workflowId]: backup },
          },
        });
      },
      drop: (owner, workflowId) => {
        const mine = get().backups[owner];
        if (!mine || !(workflowId in mine)) return;
        set({
          backups: {
            ...get().backups,
            [owner]: Object.fromEntries(
              Object.entries(mine).filter(([id]) => id !== workflowId),
            ),
          },
        });
      },
      clearAll: () => {
        set({ backups: {} });
      },
    }),
    {
      name: BUILDER_BACKUPS_STORAGE_KEY,
      // 2: scoped by person. A version 1 backup says nothing about whose it
      // is, so it is dropped rather than guessed at.
      version: 2,
      migrate: () => ({ backups: {} }),
      storage: createJSONStorage(() => guardedLocalStorage),
      partialize: (state) => ({ backups: state.backups }),
    },
  ),
);

/**
 * What a backup must look like to be offered back. Storage is this browser's,
 * not ours: an entry another build wrote, or someone edited by hand, is no
 * backup at all rather than something restored into the draft.
 */
const backupShape = z.object({
  definition: z.record(z.string(), z.unknown()),
  savedAt: z.string().refine((value) => !Number.isNaN(Date.parse(value))),
  basedOn: z.object({
    revision: z.string().nullable(),
    version: z.number(),
  }),
});

/** `owner`'s backup of a workflow, if there is one that is well formed. */
export function backupFor(
  owner: string,
  workflowId: string,
): BuilderBackup | undefined {
  const stored: unknown =
    useBuilderBackupStore.getState().backups[owner]?.[workflowId];
  if (stored === undefined) return undefined;
  return backupShape.safeParse(stored).success
    ? (stored as BuilderBackup)
    : undefined;
}

/**
 * Forget a person's backup of a workflow: their edits were saved, published
 * or discarded. Nobody signed in has no backups to forget.
 *
 * A plain function, so the write hooks can call it from `onSuccess` — which
 * runs even when the builder has already gone, as it has after Save draft and
 * leave.
 */
export function clearBuilderBackup(
  owner: string | null,
  workflowId: string,
): void {
  if (owner === null) return;
  useBuilderBackupStore.getState().drop(owner, workflowId);
}

// Backups go with the person (spec 197): emptied with every other draft when
// someone signs out or someone else signs in here.
registerPersonalDraftStore(() => useBuilderBackupStore.getState().clearAll());

/** The draft as a backup, made now. */
export function backupOf(draft: WorkflowDraft, now: Date): BuilderBackup {
  return {
    definition: toDraftDefinition(draft),
    savedAt: now.toISOString(),
    basedOn: {
      revision: draft.pending?.revision ?? null,
      version: draft.version,
    },
  };
}

/**
 * Whether a backup is worth offering against what the server holds now
 * (FR-009, US4 scenarios 2 and 4).
 *
 * Not when it is stale (the same seven days the form drafts keep), and not
 * when it says nothing the server does not already have. Otherwise, it is
 * newer when it was made on top of exactly what the server holds — nobody has
 * saved since — or, when someone has, when it was made after they saved. With
 * no time to compare against it is offered: the banner only asks, and a
 * wrongly dropped backup is work lost for good.
 */
export function shouldOfferBackup(
  backup: BuilderBackup,
  server: WorkflowDraft,
  now: Date,
): boolean {
  if (!isDraftFresh({ updatedAt: backup.savedAt }, now)) return false;
  if (sameJson(backup.definition, toDraftDefinition(server))) return false;

  const sameBase =
    backup.basedOn.revision === (server.pending?.revision ?? null) &&
    backup.basedOn.version === server.version;
  if (sameBase) return true;

  const serverSavedAt =
    server.pending?.savedAt ?? server.loaded.last_saved_at ?? null;
  if (!serverSavedAt) return true;
  return new Date(backup.savedAt).getTime() > new Date(serverSavedAt).getTime();
}

/**
 * The draft with a backup's edits in it, as unsaved changes on top of what
 * the server holds.
 *
 * Mapped through the loader, so the backup is read exactly as a saved draft
 * would be; the anchors a save quotes stay the server's, so the next Save is
 * judged against what is there now rather than what was there then.
 */
export function restoredFrom(
  current: WorkflowDraft,
  backup: BuilderBackup,
): WorkflowDraft {
  return {
    ...fromWorkflowView(withDefinition(current.loaded, backup.definition)),
    loaded: current.loaded,
    status: current.status,
    version: current.version,
    pending: current.pending,
  };
}

/** Structural equality, blind to key order. */
function sameJson(a: unknown, b: unknown): boolean {
  return stableJson(a) === stableJson(b);
}

function stableJson(value: unknown): string {
  return JSON.stringify(value, (_key, inner: unknown) =>
    inner !== null && typeof inner === 'object' && !Array.isArray(inner)
      ? Object.fromEntries(
          Object.entries(inner as Record<string, unknown>).sort(([a], [b]) =>
            a.localeCompare(b),
          ),
        )
      : inner,
  );
}
